import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { sendToTrash, requestAssetRemoval } from '../lib/api';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { toast } from 'sonner';
import { Trash2, Loader2, AlertTriangle } from 'lucide-react';
import PhotoUpload from './PhotoUpload';

export default function TrashModal({ open, onClose, asset, onSuccess }) {
  const { isAdminOrOps } = useAuth();
  const [loading, setLoading] = useState(false);
  const [trashData, setTrashData] = useState({
    reason: '',
    approved_by_note: '',
    photos: [],
  });

  const isUserAdminOrOps = isAdminOrOps();

  const resetForm = () => {
    setTrashData({
      reason: '',
      approved_by_note: '',
      photos: [],
    });
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleTrash = async () => {
    // Validate reason
    if (!trashData.reason.trim()) {
      toast.error('Please enter a reason for trashing');
      return;
    }

    // Validate photos - REQUIRED for all roles
    if (trashData.photos.length === 0) {
      toast.error('Please add at least one photo documenting the asset condition');
      return;
    }

    // For Admin/Ops direct trash, also need approved_by
    if (isUserAdminOrOps && !trashData.approved_by_note.trim()) {
      toast.error('Please enter who approved this disposal');
      return;
    }

    setLoading(true);
    try {
      if (isUserAdminOrOps) {
        // Admin/Ops can trash directly
        await sendToTrash(asset.id, {
          reason: trashData.reason,
          approved_by_note: trashData.approved_by_note,
          event_date: new Date().toISOString(),
          photos: trashData.photos,
        });
        toast.success('Asset trashed successfully!');
      } else {
        // Managers submit a trash request for approval
        await requestAssetRemoval(asset.id, {
          reason: trashData.reason,
          photos: trashData.photos,
        });
        toast.success('Trash request submitted for approval');
      }
      
      resetForm();
      onClose();
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to trash asset');
    } finally {
      setLoading(false);
    }
  };

  if (!asset) return null;

  const canSubmit = trashData.reason.trim() && 
                    trashData.photos.length > 0 && 
                    (isUserAdminOrOps ? trashData.approved_by_note.trim() : true);

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) handleClose(); }}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <Trash2 className="h-5 w-5" />
            {isUserAdminOrOps ? 'Trash Asset' : 'Request Asset Trash'}
          </DialogTitle>
          <DialogDescription>
            <span className="font-mono font-bold">{asset.barcode}</span>
            <span className="text-gray-500 ml-2">({asset.category_name || 'Unknown Category'})</span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Warning Banner */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
            <AlertTriangle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-red-800">
              <p className="font-medium">This action will mark the asset as trashed.</p>
              <p className="mt-1">Photos documenting the asset's condition are <strong>required</strong> before proceeding.</p>
            </div>
          </div>

          {/* Reason */}
          <div>
            <Label>Reason for Trashing *</Label>
            <Textarea
              value={trashData.reason}
              onChange={(e) => setTrashData(prev => ({ ...prev, reason: e.target.value }))}
              placeholder="Why is this asset being trashed? (e.g., Beyond repair, Obsolete, Damaged beyond use)"
              className="mt-1"
              data-testid="trash-reason-input"
            />
          </div>

          {/* Approved By (Admin/Ops only) */}
          {isUserAdminOrOps && (
            <div>
              <Label>Approved By *</Label>
              <Input
                value={trashData.approved_by_note}
                onChange={(e) => setTrashData(prev => ({ ...prev, approved_by_note: e.target.value }))}
                placeholder="Manager/supervisor name who approved this disposal"
                className="mt-1"
                data-testid="trash-approved-by-input"
              />
            </div>
          )}

          {/* Photo Upload - REQUIRED */}
          <div className="border-t pt-4">
            <PhotoUpload
              photos={trashData.photos}
              onPhotosChange={(photos) => setTrashData(prev => ({ ...prev, photos }))}
              required={true}
              entityType="trash"
              entityId={asset?.id || 'temp'}
              label="Disposal Photos"
              helpText="Document the asset's current condition. This is mandatory for all trash requests."
            />
          </div>
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={handleTrash}
            disabled={loading || !canSubmit}
            className={`${canSubmit ? 'bg-red-500 hover:bg-red-600' : 'bg-gray-400 cursor-not-allowed'}`}
            data-testid="confirm-trash-btn"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Processing...
              </>
            ) : (
              <>
                <Trash2 className="h-4 w-4 mr-2" />
                {isUserAdminOrOps ? 'Trash Asset' : 'Submit Request'}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
